import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ErrorBoundary } from '@sainsburys-tech/bolt'
import { useCookieConsent, useInterval } from '@sainsburys-tech/boltui-hooks'
import { canUseDom } from '@sainsburys-tech/boltui-utils'
import GoogleAdSense from '../../helpers/GoogleAdSense'
import config from '../../config'
import breakpoints from '../../config/breakpoints'
import * as Styled from './styles'

const GoogleAdSenseFooter = props => {
  const { searchTerm, pageType, categoryName, categoryAncestors, meta, noResults, isClearance, advertWidth } = props

  const [isScriptInjected, setIsScriptInjected] = useState(false)
  const [isApiReady, setIsApiReady] = useState(false)
  const [advertCount, setAdvertCount] = useState(3)
  const [smBreakpoint] = useState(canUseDom() ? window.matchMedia(breakpoints.small) : null)

  const hasUserConsent = useCookieConsent('marketing')

  const isMobileBreakpoint = () => !smBreakpoint.matches

  const injectScript = () => {
    const scriptTag = document.createElement('script')
    scriptTag.async = true
    scriptTag.defer = true
    scriptTag.setAttribute('data-name', 'google-adsense')
    scriptTag.src = `//www.google.com/adsense/search/ads.js`
    document.head.appendChild(scriptTag)
    setIsScriptInjected(true)
  }

  const setMediaQueryState = searchTerm => {
    const { advertCount } = config.adSense[pageType]

    if (isMobileBreakpoint()) {
      setAdvertCount(noResults ? advertCount.mobile.noResults : advertCount.mobile.hasResults)
    } else {
      setAdvertCount(advertCount.desktop)
    }

    executeAdSense(searchTerm)
  }

  const executeAdSense = searchTerm => {
    if (!isApiReady) return

    const testMode = window.location.hostname.toLowerCase().indexOf(config.domain.replace('www.', '')) === -1
    const pageOptionsParams = GoogleAdSense.getPageOptionsParams({
      pageType,
      categoryName,
      categoryAncestors,
      searchTerm,
      testMode,
      meta,
      noResults,
      isMobile: isMobileBreakpoint(),
      isClearance
    })
    const pageOptions = GoogleAdSense.getPageOptions(pageOptionsParams)
    const adSettings = GoogleAdSense.getAdSettings({
      container: config.adSense[pageType].elementId,
      advertCount,
      advertWidth
    })
    const isValid = GoogleAdSense.validateQuery({
      fullPageOptions: pageOptions,
      adSettings
    })

    // Execute Adsense display call
    if (isValid) {
      window._googCsa('ads', pageOptions, adSettings)
    }
  }

  // Inject script
  useEffect(() => {
    if (hasUserConsent && !isScriptInjected) injectScript()
  }, [hasUserConsent, isScriptInjected])

  // Poll for API-ready
  useInterval(
    () => {
      setIsApiReady(canUseDom() && typeof window._googCsa === 'function')
    },
    400,
    isApiReady
  )

  // Mounted
  useEffect(() => {
    if (isApiReady) {
      smBreakpoint.addListener(() => setMediaQueryState(searchTerm))
      setMediaQueryState(searchTerm)

      return () => {
        smBreakpoint.removeListener(() => setMediaQueryState())
      }
    }
  }, [isApiReady, props])

  const showLeftColumnSpacer = !noResults

  return (
    hasUserConsent && (
      <ErrorBoundary>
        <aside className={`xs-row xs-auto--negative adsense-footer${noResults ? '__no_result' : ''}`}>
          {showLeftColumnSpacer && <div className='xs-hidden lg-block lg-3' />}
          <Styled.Wrapper
            className={`
            xs-12
            ${showLeftColumnSpacer ? 'lg-9' : ''}
          `}>
            <div id={config.adSense[pageType].elementId} />
          </Styled.Wrapper>
        </aside>
      </ErrorBoundary>
    )
  )
}

GoogleAdSenseFooter.defaultProps = {
  advertWidth: '100%',
  isClearance: false,
  categoryName: '',
  categoryAncestors: [],
  pageType: '',
  meta: {}
}

GoogleAdSenseFooter.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  noResults: PropTypes.bool.isRequired,
  categoryName: PropTypes.string,
  categoryAncestors: PropTypes.array,
  pageType: PropTypes.string,
  meta: PropTypes.object,
  isClearance: PropTypes.bool,
  advertWidth: PropTypes.string
}

export default GoogleAdSenseFooter
